import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../../shared/services/config/config.service';
import { Observable } from 'rxjs';
import {
  IValidarCondutoresRequest,
  IValidarCondutoresResponse,
} from '../models/folha-dados.interface';

@Injectable({
  providedIn: 'root',
})
export class TaxisService {
  private baseUrl: string;
  private sasToken: string;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
  ) {
    this.baseUrl = this.configService.pessoaApi;
    this.sasToken =
      'sv=2022-10-07&se=2025-01-09T12%3A00%3A00Z&sr=b&sp=r&sig=abcdefg1234567890';
  }

  getTaxis(
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = '',
    sortProperty: string = '',
    placaOuPermissao: string = '',
    tipoDeUtilizacao: string = '',
    permissionario: string = '',
    marca: string = '',
    modelo: string = '',
    cor: string = '',
    anoFabricacaoInicio: string = '',
    anoFabricacaoFim: string = '',
    anoModeloInicio: string = '',
    anoModeloFim: string = '',
    tipoDeCombustivel: string = '',
    numChassi: string = '',
    primeiroEmplacamentoInicio: string = '',
    primeiroEmplacamentoFim: string = '',
    statusNomes: string[] = [],
    vidaUtilInicio: string = '',
    vidaUtilFim: string = ''
  ): Observable<string[]> {
    const params = new HttpParams()
      .set('size', pageSize.toString())
      .set('page', pageIndex.toString())
      .set('sortField', sortProperty)
      .set('sortDirection', sortDirection)
      .set('placa', placaOuPermissao)
      .set('numeroPermissao', placaOuPermissao)
      .set('TipoUtilizacao', tipoDeUtilizacao)
      .set('nomePermissionario', permissionario)
      .set('marca', marca)
      .set('modelo', modelo)
      .set('cor', cor)
      .set('anoFabricacaoInicio', anoFabricacaoInicio)
      .set('anoFabricacaoFim', anoFabricacaoFim)
      .set('anoModeloInicio', anoModeloInicio)
      .set('anoModeloFim', anoModeloFim)
      .set('tipoDeCombustivel', tipoDeCombustivel)
      .set('numChassi', numChassi)
      .set('primeiroEmplacamentoInicio', primeiroEmplacamentoInicio)
      .set('primeiroEmplacamentoFim', primeiroEmplacamentoFim)
      .set('vidaUtilInicio', vidaUtilInicio)
      .set('vidaUtilFim', vidaUtilFim)
      .set('statusNomes', statusNomes.join(','));

    return this.http.get<any[]>(this.baseUrl + `/v1/taxis`, { params });
  }

  getTaxiById(id: string) {
    return this.http.get(this.baseUrl + `/v1/taxis/${id}`);
  }

  createTaxi(data: any) {
    return this.http.post(this.baseUrl + `/v1/taxis`, data);
  }

  editTaxi(id: string, data: any) {
    return this.http.patch(this.baseUrl + `/v1/taxis/${id}`, data);
  }

  deleteTaxi(id: string) {
    return this.http.delete(this.baseUrl + `/v1/taxis/${id}`);
  }

  getHistorico(
    id: string,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
  ) {
    return this.http.get(
      this.baseUrl +
        `/v1/taxis/${id}/historico?&page=${pageIndex}&sortDirection=${sortDirection}`,
    );
  }

  getTipoUtilizacao() {
    return this.http.get(this.baseUrl + `/v1/taxis/tipos-utilizacao`);
  }

  getPermissoes() {
    return this.http.get(this.baseUrl + `/v1/taxis/permissao-taxi`);
  }

  getPermissionarios() {
    return this.http.get(this.baseUrl + `/v1/taxis/permissionarios`);
  }

  getMarcasVeiculo(searchTerm: string = '') {
    return this.http.get(this.baseUrl + `/v1/taxis/marcas?nome=${searchTerm}`);
  }

  getModelosVeiculo(searchTerm: string = '') {
    return this.http.get(this.baseUrl + `/v1/taxis/modelos?nome=${searchTerm}`);
  }

  getCoresVeiculo() {
    return this.http.get(this.baseUrl + `/v1/taxis/cores`);
  }

  getTiposCombustivel() {
    return this.http.get(this.baseUrl + `/v1/taxis/tipos-combustivel`);
  }

  getMarcasTaximetro() {
    return this.http.get(this.baseUrl + `/v1/taxis/marcas-taximetro`);
  }

  getModelosTaximetro() {
    return this.http.get(this.baseUrl + `/v1/taxis/modelos-taximetro`);
  }

  getPermissao(
    searchTerm: string = '',
    excluiSeHaPermissionarioAtivo: boolean = false,
    pageIndex: number = 0,
    pageSize: number = 10,
  ) {
    return this.http.get(
      this.baseUrl +
        `/v1/taxis/permissionarios/permissao?page=${pageIndex}` +
        `&size=${pageSize}` +
        `&numeroPermissao=${searchTerm}` +
        `&excluiSeHaPermissionarioAtivo=${excluiSeHaPermissionarioAtivo}`,
    );
  }

  performTaxiTransition(idExterno: string, transicaoId: string) {
    return this.http.post(this.baseUrl + `/v1/taxis/transicao`, {
      idExterno,
      transicaoId,
    });
  }

  getHistoricoDocumento(id: string, pageIndex: number = 0) {
    return this.http.get(
      this.baseUrl + `/v1/taxis/documentos/${id}/historico?&page=${pageIndex}`,
    );
  }

  downloadDocumento(id: string): any {
    const downloadUrl = `${this.baseUrl}/v1/taxis/documentos/${id}/download`;
    const urlWithSas = `${downloadUrl}?${this.sasToken}`;

    return this.http.get(urlWithSas, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  downloadDocumentoBlob(id: string): any {
    const downloadUrl = `${this.baseUrl}/v1/taxis/documentos/${id}/download`;
    const urlWithSas = `${downloadUrl}?${this.sasToken}`;

    return this.http.get<{ url: string }>(urlWithSas, {
      responseType: 'text' as 'json',
      observe: 'response',
    });
  }

  getTipo() {
    return this.http.get(this.baseUrl + `/v1/taxis/documentos/tipos`);
  }

  editDocumento(id: any, form: FormData) {
    return this.http.patch(this.baseUrl + `/v1/taxis/documentos/${id}`, form);
  }

  uploadDocumento(form: FormData) {
    return this.http.post(this.baseUrl + `/v1/taxis/documentos`, form);
  }

  getDocumentosPendentes(idVeiculo: string) {
    return this.http.get(
      this.baseUrl + `/v1/taxis/${idVeiculo}/documentos-pendentes`,
    );
  }

  getDocumentos(
    id: string,
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
    sortProperty: string = 'dataCadastro',
  ) {
    return this.http.get(
      this.baseUrl +
        `/v1/taxis/${id}/documentos?size=${pageSize}&page=${pageIndex}&sortField=${sortProperty}&sortDirection=${sortDirection}`,
    );
  }

  deleteDocumento(id: string) {
    return this.http.delete(this.baseUrl + `/v1/taxis/documentos/${id}`);
  }

  signDocument(form: any) {
    return this.http.post(this.baseUrl + `/v1/taxis/documentos/assinar`, form);
  }

  createAssinantes(form: any) {
    return this.http.post(this.baseUrl + `/v1/taxis/assinantes`, form);
  }

  enviaEmailBHTrans(form: any) {
    return this.http.post(this.baseUrl + `/v1/taxis/email-azure`, form);
  }

  editAssinantes(form: any) {
    return this.http.patch(this.baseUrl + `/v1/taxis/assinantes`, form);
  }

  getAssinaturasPendentes(
    id: string,
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
    sortProperty: string = 'dataCadastro',
  ) {
    return this.http.get(
      this.baseUrl +
        `/v1/taxis/veiculos/${id}/assinantes?size=${pageSize}&page=${pageIndex}&sortField=${sortProperty}&sortDirection=${sortDirection}`,
    );
  }

  getAssinantesByDocId(
    docId: string,
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
    sortProperty: string = 'dataCadastro',
  ) {
    return this.http.get(
      this.baseUrl +
        `/v1/taxis/documentos/${docId}/assinantes?size=${pageSize}&page=${pageIndex}&sortField=${sortProperty}&sortDirection=${sortDirection}`,
    );
  }

  getPoertador(numeroPermissao: any) {
    const body = {
      numeroPermissao: numeroPermissao,
    };

    return this.http.post(
      this.baseUrl + `/v1/taxis/permissoes-auxiliares`,
      body,
    );
  }

  // §2.1 — valida os dados mínimos dos condutores selecionados antes da emissão.
  // Devolve 200 com diagnóstico (apto global + apto por condutor + pendencias[]),
  // não 400. Ver IValidarCondutoresResponse.
  validarCondutoresFolhaDados(body: IValidarCondutoresRequest) {
    return this.http.post<IValidarCondutoresResponse>(
      this.baseUrl + `/v1/taxis/folha-dados/validar-condutores`,
      body,
    );
  }

  getAssinaturasGlobais(
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'asc',
    sortProperty: string,
    status: string,
    dataAssinatura_start: string,
    dataAssinatura_end: string,
    dataSolicitacao_start: string,
    dataSolicitacao_end: string,
    funcionalidade: string,
    tipoDocumento: string,
    resquerenteAssinante: string,
  ) {
    const params = new HttpParams()
      .set('size', pageSize.toString())
      .set('page', pageIndex.toString())
      .set('sortField', sortProperty)
      .set('sortDirection', sortDirection)
      .set('funcionalidade', funcionalidade)
      .set('tipoDocumento', tipoDocumento)
      .set('status', status)
      .set('dataSolicitacao_start', dataSolicitacao_start)
      .set('dataSolicitacao_end', dataSolicitacao_end)
      .set('dataAssinatura_start', dataAssinatura_start)
      .set('dataAssinatura_end', dataAssinatura_end)
      .set('requerenteNome', resquerenteAssinante)
      .set('assinanteNome', resquerenteAssinante);

    return this.http.get<any[]>(
      this.baseUrl + `/v1/taxis/agregador-assinatura?`,
      { params },
    );
  }

  getFuncionalidade() {
    return this.http.get(
      this.baseUrl + `/v1/taxis/agregador-assinatura/funcionalidades`,
    );
  }

  getTipoDocumento() {
    return this.http.get(
      this.baseUrl + `/v1/taxis/agregador-assinatura/tipos-documento`,
    );
  }

  assinar(data: any) {
    return this.http.post(
      this.baseUrl + `/v1/taxis/agregador-assinatura/assinar`,
      data,
    );
  }

  getPossivelEmitirCarteira(id: any) {
    return this.http.get(
      this.baseUrl + `/v1/taxis/check-emitir-carteira/${id}`,
    );
  }

  getPossivelEmitirFolha(id: any) {
    return this.http.get(this.baseUrl + `/v1/taxis/check-emitir-folha/${id}`);
  }

  getDocumentoId(id: any) {
    return this.http.get(this.baseUrl + `/v1/taxis/documentos/${id}`);
  }

  getPossivelEmitirCertificado(id: any) {
    return this.http.get(this.baseUrl + `/v1/taxis/check-emitir-reserva/${id}`);
  }

  getPossivelEmitirExtratoPendencia(id: any) {
    return this.http.get(
      this.baseUrl + `/v1/taxis/check-emitir-extrato-pendencias/${id}`,
    );
  }

  downloadExportExcel(
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = '',
    sortProperty: string = '',
    placaOuPermissao: string = '',
    tipoDeUtilizacao: string = '',
    permissionario: string = '',
    marca: string = '',
    modelo: string = '',
    cor: string = '',
    anoFabricacaoInicio: string = '',
    anoFabricacaoFim: string = '',
    anoModeloInicio: string = '',
    anoModeloFim: string = '',
    tipoDeCombustivel: string = '',
    numChassi: string = '',
    primeiroEmplacamentoInicio: string = '',
    primeiroEmplacamentoFim: string = '',
    statusNomes: string[] = [],
    vidaUtilInicio: string = '',
    vidaUtilFim: string = '',
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('size', pageSize.toString())
      .set('page', pageIndex.toString())
      .set('sortField', sortProperty)
      .set('sortDirection', sortDirection)
      .set('placa', placaOuPermissao)
      .set('numeroPermissao', placaOuPermissao)
      .set('TipoUtilizacao', tipoDeUtilizacao)
      .set('nomePermissionario', permissionario)
      .set('marca', marca)
      .set('modelo', modelo)
      .set('cor', cor)
      .set('anoFabricacaoInicio', anoFabricacaoInicio)
      .set('anoFabricacaoFim', anoFabricacaoFim)
      .set('anoModeloInicio', anoModeloInicio)
      .set('anoModeloFim', anoModeloFim)
      .set('tipoDeCombustivel', tipoDeCombustivel)
      .set('numChassi', numChassi)
      .set('primeiroEmplacamentoInicio', primeiroEmplacamentoInicio)
      .set('primeiroEmplacamentoFim', primeiroEmplacamentoFim)
      .set('vidaUtilInicio', vidaUtilInicio)
      .set('vidaUtilFim', vidaUtilFim)
      .set('statusNomes', statusNomes.join(','));

    const url = `${this.baseUrl}/v1/taxis/exportar-dados-csv`;

    return this.http.get<Blob>(url, {
      params,
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }
}
